import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Target, Award } from 'lucide-react';
import { getProgram, updateProgram, getBadges, BadgeData } from '@/lib/firebase';

interface ProgramMilestone {
  programDay: number;
  allTasksRequired: boolean;
  badge: {
    id?: string;
    colour: string;
    description: string;
    enabled: boolean;
    imageUrl: string;
    key: string;
    name: string;
    type: string;
  };
}

interface ProgramMilestonesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programId: string;
  programName: string;
}

interface ProgramComponent {
  componentType: string;
  startOnDay?: number;
  tasks?: any[];
}

const calculateProgramLength = (components: ProgramComponent[]): number => {
  if (!components || components.length === 0) return 1;
  
  let maxDay = 1;
  
  for (const component of components) {
    if (component.componentType === 'videoSeries' || component.componentType === 'dynamicTask') {
      const startDay = component.startOnDay || 1;
      const taskCount = component.tasks?.length || 0;
      
      if (taskCount > 0) {
        const componentEndDay = startDay + taskCount - 1;
        maxDay = Math.max(maxDay, componentEndDay);
      }
    }
  }
  
  return maxDay;
};

export default function ProgramMilestonesModal({ 
  open, 
  onOpenChange, 
  programId,
  programName 
}: ProgramMilestonesModalProps) {
  const [milestones, setMilestones] = useState<ProgramMilestone[]>([]);
  const [badges, setBadges] = useState<BadgeData[]>([]);
  const [programLength, setProgramLength] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && programId) {
      loadData();
    }
  }, [open, programId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [badgesData, programData] = await Promise.all([
        getBadges(),
        getProgram(programId)
      ]);
      
      setBadges(badgesData.filter(b => b.enabled));
      
      if (programData) {
        const existingMilestones = (programData as any).programMilestones || [];
        setMilestones(existingMilestones);
        
        const components = (programData as any).components || [];
        const calculatedLength = calculateProgramLength(components);
        setProgramLength(calculatedLength);
      }
    } catch (error: any) {
      toast({
        title: "Failed to load data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMilestone = () => {
    const newMilestone: ProgramMilestone = {
      programDay: 1,
      allTasksRequired: true,
      badge: {
        colour: '',
        description: '',
        enabled: true,
        imageUrl: '',
        key: '',
        name: '',
        type: 'program'
      }
    };
    setMilestones([...milestones, newMilestone]);
  };

  const handleRemoveMilestone = (index: number) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const handleMilestoneChange = (index: number, field: keyof ProgramMilestone, value: any) => {
    const updated = [...milestones];
    updated[index] = { ...updated[index], [field]: value };
    setMilestones(updated);
  };

  const handleBadgeSelect = (index: number, badgeId: string) => {
    const selectedBadge = badges.find(b => b.id === badgeId);
    if (selectedBadge) {
      const updated = [...milestones];
      updated[index] = {
        ...updated[index],
        badge: {
          id: selectedBadge.id,
          colour: selectedBadge.colour,
          description: selectedBadge.description,
          enabled: selectedBadge.enabled,
          imageUrl: selectedBadge.imageUrl || '',
          key: selectedBadge.key,
          name: selectedBadge.name,
          type: selectedBadge.type
        }
      };
      setMilestones(updated);
    }
  };

  const handleSave = async () => {
    const invalidMilestones = milestones.filter(m => !m.badge.key || m.programDay < 1 || m.programDay > programLength);
    if (invalidMilestones.length > 0) {
      toast({
        title: "Validation Error",
        description: `All milestones must have a badge selected and a valid program day (1-${programLength}).`,
        variant: "destructive",
      });
      return;
    }

    const duplicateDays = milestones.filter((m, i, arr) => 
      arr.findIndex(a => a.programDay === m.programDay) !== i
    );
    if (duplicateDays.length > 0) {
      toast({
        title: "Validation Error",
        description: "Each milestone must have a unique program day.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);
      
      const sortedMilestones = [...milestones].sort((a, b) => a.programDay - b.programDay);
      
      await updateProgram(programId, { programMilestones: sortedMilestones });

      toast({
        title: "Milestones saved",
        description: `${milestones.length} milestone(s) saved successfully.`,
      });
      
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Failed to save milestones",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getBadgeIdFromKey = (key: string) => {
    const badge = badges.find(b => b.key === key);
    return badge?.id || '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Target className="w-5 h-5" />
            <span>Program Milestones - {programName}</span>
          </DialogTitle>
          {!loading && (
            <p className="text-sm text-slate-600">
              Program length: {programLength} day{programLength !== 1 ? 's' : ''}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {milestones.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">No milestones defined for this program.</p>
                <Button onClick={handleAddMilestone} data-testid="button-add-first-milestone">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Milestone
                </Button>
              </div>
            ) : (
              <>
                {milestones.map((milestone, index) => (
                  <Card key={index} className="relative">
                    <CardContent className="pt-6">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveMilestone(index)}
                        data-testid={`button-remove-milestone-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor={`programDay-${index}`}>Program Day *</Label>
                          <Select
                            value={milestone.programDay.toString()}
                            onValueChange={(value) => handleMilestoneChange(index, 'programDay', parseInt(value))}
                          >
                            <SelectTrigger data-testid={`select-program-day-${index}`}>
                              <SelectValue placeholder="Select day" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: programLength }, (_, i) => i + 1).map((day) => (
                                <SelectItem key={day} value={day.toString()}>
                                  Day {day}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor={`badge-${index}`}>Badge *</Label>
                          <Select
                            value={getBadgeIdFromKey(milestone.badge.key)}
                            onValueChange={(value) => handleBadgeSelect(index, value)}
                          >
                            <SelectTrigger data-testid={`select-badge-${index}`}>
                              <SelectValue placeholder="Select a badge" />
                            </SelectTrigger>
                            <SelectContent>
                              {badges.map((badge) => (
                                <SelectItem key={badge.id} value={badge.id!}>
                                  <div className="flex items-center space-x-2">
                                    {badge.imageUrl && (
                                      <img 
                                        src={badge.imageUrl} 
                                        alt={badge.name} 
                                        className="w-5 h-5 rounded"
                                      />
                                    )}
                                    <span>{badge.name}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center space-x-2 pt-6">
                          <Switch
                            id={`allTasksRequired-${index}`}
                            checked={milestone.allTasksRequired}
                            onCheckedChange={(checked) => handleMilestoneChange(index, 'allTasksRequired', checked)}
                            data-testid={`switch-all-tasks-${index}`}
                          />
                          <Label htmlFor={`allTasksRequired-${index}`} className="text-sm">
                            All tasks required
                          </Label>
                        </div>
                      </div>

                      {milestone.badge.key && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center space-x-3">
                          {milestone.badge.imageUrl && (
                            <img 
                              src={milestone.badge.imageUrl} 
                              alt={milestone.badge.name} 
                              className="w-10 h-10 rounded"
                            />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{milestone.badge.name}</p>
                            <p className="text-sm text-slate-600">{milestone.badge.description}</p>
                          </div>
                          <div 
                            className="ml-auto w-4 h-4 rounded-full"
                            style={{ backgroundColor: milestone.badge.colour }}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleAddMilestone}
                  data-testid="button-add-milestone"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Milestone
                </Button>
              </>
            )}

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                data-testid="button-save-milestones"
              >
                {saving ? 'Saving...' : 'Save Milestones'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
